import jsPDF from 'jspdf';

/**
 * Detect whether any string in the plant data contains characters outside
 * the Latin + Latin-Extended + common punctuation ranges that jsPDF's
 * default Helvetica font can render. If yes, we fall back to an HTML +
 * html2canvas rendering path so the browser's system font handles CJK,
 * emoji, Arabic, Hebrew, etc.
 */
function _needsUnicodeFont(plants) {
  const nonLatin = /[^\u0000-\u024F\u1E00-\u1EFF\u2000-\u206F\u2100-\u214F°×#]/;
  for (const p of plants) {
    if (nonLatin.test(p.common_name || '')) return true;
    if (nonLatin.test(p.scientific_name || '')) return true;
    const cd = p.care_details || {};
    for (const v of Object.values(cd)) {
      if (typeof v === 'string' && nonLatin.test(v)) return true;
    }
  }
  return false;
}

/**
 * Fast text-based PDF using jsPDF's built-in Helvetica. Only safe for
 * Latin-alphabet content. Text is selectable/searchable in the output.
 */
function _exportPlantsToPDFText(plants) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };
  const wrap = (text, width) => doc.splitTextToSize(text || '', width);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(45, 82, 60);
  doc.text('My Personal Plant Inventory', margin, y);
  y += 26;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(90, 90, 90);
  doc.text(`${plants.length} plant${plants.length !== 1 ? 's' : ''} \u00b7 Exported ${new Date().toLocaleDateString()}`, margin, y);
  y += 22;

  const sorted = [...plants].sort((a, b) =>
    (a.common_name || '').localeCompare(b.common_name || '', undefined, { sensitivity: 'base' })
  );
  const FIELDS = [
    ['Family', 'family'], ['Type', 'plant_type'], ['Watering', 'watering'],
    ['Light', 'light'], ['Humidity', 'humidity'], ['Temperature', 'temperature'],
    ['Toxicity', 'toxicity'], ['Toxicity notes', 'toxicity_notes'],
    ['Environment', 'environment'], ['Growth rate', 'growth_rate'],
    ['Dormancy', 'dormancy_notes'],
  ];

  sorted.forEach((plant, idx) => {
    const cd = plant.care_details || {};
    const rows = FIELDS
      .map(([label, key]) => (cd[key] ? [label, String(cd[key])] : null))
      .filter(Boolean);
    const heightNeeded = 34 + rows.length * 14 + 16;
    ensureSpace(heightNeeded);
    if (idx > 0) {
      doc.setDrawColor(220, 228, 220);
      doc.setLineWidth(0.5);
      doc.line(margin, y - 8, pageWidth - margin, y - 8);
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(45, 82, 60);
    doc.text(plant.common_name || 'Unknown plant', margin, y);
    y += 16;
    if (plant.scientific_name) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.setTextColor(110, 110, 110);
      doc.text(plant.scientific_name, margin, y);
      y += 14;
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(45, 45, 45);
    const labelColX = margin;
    const valueColX = margin + 90;
    const valueWidth = contentWidth - 90;
    rows.forEach(([label, value]) => {
      const lines = wrap(value, valueWidth);
      ensureSpace(lines.length * 12 + 2);
      doc.setFont('helvetica', 'bold');
      doc.text(label, labelColX, y);
      doc.setFont('helvetica', 'normal');
      doc.text(lines, valueColX, y);
      y += lines.length * 12 + 2;
    });
    y += 10;
  });

  doc.save(`plants_${new Date().toISOString().split('T')[0]}.pdf`);
}

/**
 * HTML-based PDF via html2canvas. Handles ANY Unicode script (Japanese,
 * Korean, Chinese, Arabic, Hebrew, Cyrillic, Devanagari, emoji, …) because
 * the browser's native font stack renders the text before we snapshot it.
 * Text isn't selectable in the output, and file size is larger, but
 * rendering fidelity is perfect for the user's locale.
 */
async function _exportPlantsToPDFHTML(plants) {
  const html2canvas = (await import('html2canvas')).default;

  // Build a hidden off-screen container styled like a printable page.
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText = [
    'position: fixed',
    'left: -10000px', 'top: 0',
    'width: 720px',
    'padding: 32px',
    'background: white',
    // Rely on browser default fonts — including the OS's CJK/emoji fallbacks
    'font-family: system-ui, -apple-system, "Segoe UI", "Noto Sans", "Noto Sans CJK JP", "Noto Sans CJK SC", "Noto Sans CJK KR", "Hiragino Sans", "Yu Gothic", "Microsoft YaHei", "Malgun Gothic", sans-serif',
    'color: #2d2d2d',
    'font-size: 12px',
    'line-height: 1.5',
  ].join(';');

  const escape = (s) => (s == null ? '' : String(s))
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  const sorted = [...plants].sort((a, b) =>
    (a.common_name || '').localeCompare(b.common_name || '', undefined, { sensitivity: 'base' })
  );
  const FIELDS = [
    ['Family', 'family'], ['Type', 'plant_type'], ['Watering', 'watering'],
    ['Light', 'light'], ['Humidity', 'humidity'], ['Temperature', 'temperature'],
    ['Toxicity', 'toxicity'], ['Toxicity notes', 'toxicity_notes'],
    ['Environment', 'environment'], ['Growth rate', 'growth_rate'],
    ['Dormancy', 'dormancy_notes'],
  ];

  const plantsHtml = sorted.map((plant, idx) => {
    const cd = plant.care_details || {};
    const rows = FIELDS
      .filter(([, k]) => cd[k])
      .map(([label, k]) => `<tr><td style="font-weight:600;padding:2px 12px 2px 0;vertical-align:top;white-space:nowrap;">${escape(label)}</td><td style="padding:2px 0;">${escape(cd[k])}</td></tr>`)
      .join('');
    return `
      <div style="${idx > 0 ? 'border-top:1px solid #dce4dc;padding-top:12px;margin-top:12px;' : ''}">
        <div style="font-weight:700;font-size:14px;color:#2d523c;">${escape(plant.common_name || 'Unknown plant')}</div>
        ${plant.scientific_name ? `<div style="font-style:italic;color:#6e6e6e;margin-bottom:6px;">${escape(plant.scientific_name)}</div>` : ''}
        <table style="border-collapse:collapse;">${rows}</table>
      </div>`;
  }).join('');

  host.innerHTML = `
    <h1 style="font-size:22px;color:#2d523c;margin:0 0 6px 0;">My Personal Plant Inventory</h1>
    <div style="color:#5a5a5a;font-size:11px;margin-bottom:16px;">
      ${plants.length} plant${plants.length !== 1 ? 's' : ''} · Exported ${new Date().toLocaleDateString()}
    </div>
    ${plantsHtml}
  `;
  document.body.appendChild(host);

  try {
    const canvas = await html2canvas(host, {
      scale: 2, // Retina crispness for the resulting image
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false,
    });

    // Letter page in points: 612x792. We'll fit the canvas width to the
    // page's content area and slice it across pages if it's taller.
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 24;
    const imgWidth = pageWidth - margin * 2;
    const scale = imgWidth / canvas.width;
    const fullImgHeight = canvas.height * scale;
    const usablePageHeight = pageHeight - margin * 2;

    if (fullImgHeight <= usablePageHeight) {
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      doc.addImage(dataUrl, 'JPEG', margin, margin, imgWidth, fullImgHeight);
    } else {
      // Slice the source canvas into page-sized chunks
      const chunkHeightSrc = Math.floor(usablePageHeight / scale);
      let yOffset = 0;
      let first = true;
      while (yOffset < canvas.height) {
        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = Math.min(chunkHeightSrc, canvas.height - yOffset);
        const ctx = slice.getContext('2d');
        ctx.drawImage(canvas, 0, yOffset, slice.width, slice.height, 0, 0, slice.width, slice.height);
        if (!first) doc.addPage();
        const dataUrl = slice.toDataURL('image/jpeg', 0.88);
        doc.addImage(dataUrl, 'JPEG', margin, margin, imgWidth, slice.height * scale);
        yOffset += slice.height;
        first = false;
      }
    }

    doc.save(`plants_${new Date().toISOString().split('T')[0]}.pdf`);
  } finally {
    host.remove();
  }
}

/**
 * Generate a printable PDF of the user's plant collection.
 * Client-side only — works for both Drive mode and Local mode. Automatically
 * picks between a fast text-based render (Latin content) and an HTML-canvas
 * render (any Unicode content: CJK, Arabic, emoji, etc.).
 */
export async function exportPlantsToPDF(plants) {
  if (!plants || plants.length === 0) {
    throw new Error('No plants to export');
  }
  if (_needsUnicodeFont(plants)) {
    await _exportPlantsToPDFHTML(plants);
  } else {
    _exportPlantsToPDFText(plants);
  }
}


/**
 * Export a plant's Care Log Growth Timeline as a printable PDF collage.
 * Only entries with a photo are included, ordered chronologically
 * (oldest → newest) so the PDF reads like a time-lapse. Uses html2canvas
 * for a font-safe render that handles CJK/emoji plant names cleanly.
 *
 * `resolvePhotoSrc(entry)` is passed in by the caller because photo
 * fetching differs between Drive (auth-fetched blob URL) and Local mode
 * (IndexedDB base64 → data URL). The caller is responsible for revoking
 * any blob URLs it hands us; we make no assumption about their lifetime.
 *
 * Signature:
 *   exportCareTimelineToPDF({ plant, entries, resolvePhotoSrc })
 * Where:
 *   plant             = { common_name, scientific_name }
 *   entries           = ordered array of { entry_id, action, at, note?, photo_drive_id }
 *   resolvePhotoSrc   = async (entry) => data-URL or blob-URL string
 */
export async function exportCareTimelineToPDF({ plant, entries, resolvePhotoSrc }) {
  if (!entries || entries.length === 0) {
    throw new Error('No timeline photos to export');
  }
  const { default: html2canvas } = await import('html2canvas');

  // Resolve every photo to a data URL upfront so html2canvas doesn't have
  // to deal with CORS on Drive-proxied blob URLs mid-render.
  const resolved = await Promise.all(
    entries.map(async (e) => {
      try {
        const src = await resolvePhotoSrc(e);
        return { ...e, _src: src };
      } catch (_err) {
        return { ...e, _src: null };
      }
    })
  );
  const usable = resolved.filter((r) => r._src);
  if (usable.length === 0) throw new Error('No timeline photos could be loaded');

  const host = document.createElement('div');
  host.style.cssText = [
    'position:absolute', 'left:-99999px', 'top:0',
    'width:700px', 'padding:24px', 'background:#ffffff',
    'font-family: system-ui, -apple-system, "Segoe UI", "Noto Sans", "Noto Sans CJK JP", "Noto Sans CJK SC", "Noto Sans CJK KR", "Hiragino Sans", "Yu Gothic", "Microsoft YaHei", "Malgun Gothic", sans-serif',
    'color:#2d2d2d', 'font-size:12px', 'line-height:1.5',
  ].join(';');
  const escape = (s) => (s == null ? '' : String(s))
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  const cellsHtml = usable.map((e) => {
    const dateStr = new Date(e.at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    return `
      <div style="width:calc(50% - 8px);margin-bottom:16px;box-sizing:border-box;">
        <div style="width:100%;padding-top:100%;position:relative;background:#f2f5f2;border-radius:6px;overflow:hidden;">
          <img src="${escape(e._src)}" crossorigin="anonymous" style="position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;" />
        </div>
        <div style="margin-top:6px;font-size:11px;">
          <div style="font-weight:600;color:#2d523c;text-transform:capitalize;">${escape(e.action)} · <span style="color:#6e6e6e;font-weight:400;">${escape(dateStr)}</span></div>
          ${e.note ? `<div style="color:#5a5a5a;margin-top:2px;font-style:italic;">${escape(e.note)}</div>` : ''}
        </div>
      </div>`;
  }).join('');
  host.innerHTML = `
    <h1 style="font-size:22px;color:#2d523c;margin:0 0 4px 0;">${escape(plant.common_name || 'Plant')} — Growth timeline</h1>
    ${plant.scientific_name ? `<div style="font-style:italic;color:#6e6e6e;margin-bottom:16px;">${escape(plant.scientific_name)}</div>` : '<div style="margin-bottom:16px;"></div>'}
    <div style="color:#5a5a5a;font-size:11px;margin-bottom:16px;">
      ${usable.length} photo${usable.length !== 1 ? 's' : ''} · Exported ${new Date().toLocaleDateString()}
    </div>
    <div style="display:flex;flex-wrap:wrap;gap:16px;">${cellsHtml}</div>
  `;
  document.body.appendChild(host);

  try {
    // Wait a tick for every <img> to actually paint before snapshotting.
    await new Promise((r) => setTimeout(r, 100));
    await Promise.all(Array.from(host.querySelectorAll('img')).map((img) => {
      if (img.complete) return null;
      return new Promise((r) => { img.onload = r; img.onerror = r; });
    }));
    const canvas = await html2canvas(host, {
      scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false,
    });
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 24;
    const imgWidth = pageWidth - margin * 2;
    const scale = imgWidth / canvas.width;
    const fullImgHeight = canvas.height * scale;
    const usablePageHeight = pageHeight - margin * 2;
    if (fullImgHeight <= usablePageHeight) {
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      doc.addImage(dataUrl, 'JPEG', margin, margin, imgWidth, fullImgHeight);
    } else {
      const chunkHeightSrc = Math.floor(usablePageHeight / scale);
      let yOffset = 0;
      let first = true;
      while (yOffset < canvas.height) {
        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = Math.min(chunkHeightSrc, canvas.height - yOffset);
        const ctx = slice.getContext('2d');
        ctx.drawImage(canvas, 0, yOffset, slice.width, slice.height, 0, 0, slice.width, slice.height);
        if (!first) doc.addPage();
        const dataUrl = slice.toDataURL('image/jpeg', 0.88);
        doc.addImage(dataUrl, 'JPEG', margin, margin, imgWidth, slice.height * scale);
        yOffset += slice.height;
        first = false;
      }
    }
    const slug = (plant.common_name || 'plant').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'plant';
    doc.save(`${slug}_timeline_${new Date().toISOString().split('T')[0]}.pdf`);
  } finally {
    host.remove();
  }
}
